import { memo, useState } from 'react';
import dayjs from 'dayjs';
import { Tag } from 'antd';
import { faCalendar, faFlask, faMap, faSeedling, faUsers, faUsersLine, faVihara } from '@fortawesome/free-solid-svg-icons';

//APP
import { useFormOsContext } from '../../context/FormOsContext';
import { dados_status, statusIcones, STATUS_COLETADA } from '../../../../meta';

//COMPONENTS
import Tabs from '../../../../../../../../../../Components/Tabs';
import Input from '../../../../../../../../../../Components/CustomUi/input';
import DatePickerCustom from '../../../../../../../../../../Components/CustomUi/datePicker';
import { Render } from '../../../../../../../../../../Components/CustomUi/render';
import { Select } from '../../../../../../../../../../Components/CustomUi/Select';
import { SelectMulti } from '../../../../../../../../../../Components/CustomUi/SelectMulti';

const AREAS_AMOSTRA = [
    { icone: '', descricao: '50 ha' },
    { icone: '', descricao: '20 ha' },
]

/**
 * Metade esquerda do formulário: campos de cadastro da OS.
 * Consome o estado via context (sem prop drilling). Memoizado para não
 * re-renderizar junto com a metade "Análises".
 */
export const CamposFormulario = memo(() => {

    const {
        campos,
        setCampo,
        setResponsaveis,
        selecionarPropriedade,
        selecionarPropriedadeId,
        matricula,
        area_propriedade,
        talhoes,
        carregando_talhoes,
        propriedades_ativas,
        carregando_propriedades,
        data_safra_consultoria,
        data_equipe,
        data_laboratorios,
        data_tipos_analise,
        data_representantes,
        data_cliente,
        isEdicao,
    } = useFormOsContext()

    //#region ESTADO LOCAL DE ABERTURA DOS SELECTS
    const [openStatus, setOpenStatus] = useState(false)
    const [openRepresentante, setOpenRepresentante] = useState(false)
    const [openTipoAnalise, setOpenTipoAnalise] = useState(false)
    const [openPropriedade, setOpenPropriedade] = useState(false)
    const [openTalhao, setOpenTalhao] = useState(false)
    const [openLaboratorio, setOpenLaboratorio] = useState(false)
    const [openSafra, setOpenSafra] = useState(false)
    const [openResponsaveis, setOpenResponsaveis] = useState(false)

    const ehGrupo = data_cliente?.tipo === 'G'

    /** DatePicker do antd entrega um Dayjs; guardamos como ISO string (ou '') no estado. */
    const aoMudarData = (chave: 'data_da_os' | 'coleta_liberada' | 'coletada_em') => (valor: any) => {
        setCampo(chave, valor ? dayjs(valor).toISOString() : '')
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Render condition={isEdicao}>
                <Select
                    label='Status atual'
                    placeholder='Selecione o status atual'
                    icon={statusIcones[campos.status_atual_id as keyof typeof statusIcones]}
                    value={campos.status_atual}
                    onChange={(v) => setCampo('status_atual', v as string)}
                    onChangeID={(id) => setCampo('status_atual_id', id as number)}
                    open={openStatus}
                    trigger={setOpenStatus}
                    items={dados_status}
                    descricaoItem='nome'
                    idItem='cod'
                    obrigatorio
                />
            </Render>

            <Render condition={ehGrupo}>
                <Select
                    label='Representante'
                    value={campos.representante_selecionado}
                    onChange={(v) => setCampo('representante_selecionado', v as string)}
                    onChangeID={(id) => setCampo('representante_selecionado_id', id as number)}
                    open={openRepresentante}
                    trigger={setOpenRepresentante}
                    items={data_representantes}
                    descricaoItem='nome'
                    idItem='cod'
                />
            </Render>

            {/* Tipo da análise é definido pelo submódulo ativo (ex: Solo) — vem
                pré-selecionado e travado; não é escolha do usuário. */}
            <Select
                label='Tipo da análise'
                placeholder='Selecione o tipo da análise'
                icon={faFlask}
                value={campos.tipo_de_analise}
                onChange={(v) => setCampo('tipo_de_analise', v as string)}
                onChangeID={(id) => setCampo('tipo_de_analise_id', id as number)}
                open={openTipoAnalise}
                trigger={setOpenTipoAnalise}
                items={data_tipos_analise}
                descricaoItem='nome'
                idItem='cod'
                disabled
                obrigatorio
            />

            <DatePickerCustom
                label='Data O.S'
                icon={faCalendar}
                value={campos.data_da_os ? dayjs(campos.data_da_os) : null}
                onChange={aoMudarData('data_da_os')}
                width={150}
                obrigatorio
            />

            <div className='detalhe__propriedade'>
                <Select
                    label='Propriedade'
                    placeholder='Selecione a propriedade'
                    icon={faVihara}
                    value={campos.propriedade}
                    onChange={(v) => selecionarPropriedade(v as string)}
                    onChangeID={(id) => selecionarPropriedadeId(id as number)}
                    open={openPropriedade}
                    trigger={setOpenPropriedade}
                    items={propriedades_ativas}
                    descricaoItem='nome'
                    idItem='cod'
                    loading={carregando_propriedades}
                    obrigatorio
                />
                <div style={{ marginTop: 6 }}>
                    <Tag style={{ border: '1px solid #0958D9', color: '#0958D9' }} color='#E6F4FF'>
                        Matrícula: {matricula}
                    </Tag>
                    <Tag style={{ border: '1px solid #B7EB8F', color: '#52C41A' }} color='#F6FFED'>
                        Área: {area_propriedade} ha
                    </Tag>
                </div>
            </div>

            <Input
                label='Outros solicitantes'
                icon={faUsers}
                placeholder='Digite os outros solicitantes'
                value={campos.outros_solicitantes}
                onChange={(v) => setCampo('outros_solicitantes', v)}
            />

            <Select
                label='Talhão'
                placeholder='Selecione o talhão'
                icon={faMap}
                value={campos.talhao}
                onChange={(v) => setCampo('talhao', v as string)}
                onChangeID={(id) => setCampo('talhao_id', id as number)}
                open={openTalhao}
                trigger={setOpenTalhao}
                items={talhoes}
                descricaoItem='nome'
                idItem='cod'
                hasTalhaoSVG
                talhaoSvgPath={['coordenadas']}
                maxPontosSvg={200}
                disabled={campos.propriedade_id === null}
                loading={carregando_talhoes}
                obrigatorio
            />

            <Select
                label='Laboratório'
                placeholder='Selecione o laboratório'
                icon={faFlask}
                value={campos.laboratorio}
                onChange={(v) => setCampo('laboratorio', v as string)}
                onChangeID={(id) => setCampo('laboratorio_id', id as number)}
                open={openLaboratorio}
                trigger={setOpenLaboratorio}
                items={data_laboratorios}
                descricaoItem='nome'
                idItem='cod'
                obrigatorio
            />

            <Select
                label='Safra consultoria'
                placeholder='Selecione a safra consultoria'
                icon={faSeedling}
                value={campos.safra_consultoria}
                onChange={(v) => setCampo('safra_consultoria', v as string)}
                onChangeID={(id) => setCampo('safra_consultoria_id', id as number)}
                open={openSafra}
                trigger={setOpenSafra}
                items={[...(data_safra_consultoria ?? [])].sort((a, b) => b.cod - a.cod)}
                descricaoItem='nome'
                idItem='cod'
                obrigatorio
            />

            <div>
                <label
                    style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#2D3436',
                        display: 'inline-flex',
                        gap: 5,
                    }}
                >
                    Área de amostra no laudo
                </label>
                <Tabs
                    tabs={AREAS_AMOSTRA}
                    selecionado={campos.area_amostra_laudo}
                    onChange={(v) => setCampo('area_amostra_laudo', v as any)}
                    trackWidth={55}
                    width={120}
                />
            </div>

            <SelectMulti
                label='Responsáveis pela coleta'
                placeholder='Selecione o responsável pela coleta'
                icon={faUsersLine}
                open={openResponsaveis}
                trigger={setOpenResponsaveis}
                items={data_equipe}
                descricaoItem='usuario_nome'
                idItem='cod_func'
                selecteds={campos.responsaveis_selecionados}
                onChange={setResponsaveis}
                obrigatorio
            />

            <div style={{ display: 'flex', gap: 10 }}>
                <DatePickerCustom
                    label='Coleta liberada'
                    icon={faCalendar}
                    value={campos.coleta_liberada ? dayjs(campos.coleta_liberada) : null}
                    onChange={aoMudarData('coleta_liberada')}
                    width={180}
                    obrigatorio
                />
                {/* Data efetiva da coleta: só faz sentido depois que a coleta aconteceu.
                    Aparece apenas ao editar uma OS que já está no status COLETADA. */}
                <Render condition={isEdicao && campos.status_atual_id === STATUS_COLETADA}>
                    <DatePickerCustom
                        label='Coletado em'
                        icon={faCalendar}
                        value={campos.coletada_em ? dayjs(campos.coletada_em) : null}
                        onChange={aoMudarData('coletada_em')}
                        width={180}
                        obrigatorio
                    />
                </Render>
            </div>
        </div>
    )
})

CamposFormulario.displayName = 'CamposFormulario'
